/**
 * Monthly Revenue analytics tab.
 *
 * Vertical bars for the 12 months of the selected FY + dashed target line.
 * Extracted from `pages/Analytics.js` to keep each tab in its own module
 * (mitigates the visual-edits Babel plugin's recursion crash on large files).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import FYSelect, { CURRENT_FY } from '@/components/FYSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRIMARY = '#0BA888';

const fmtINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;
const fmtINRCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${Math.round(v)}`;
};

const MonthBar = ({ m, maxValue, current }) => {
  const heightPct = (m.revenue / maxValue) * 100;
  const isCurrent = current && current.year === m.year && current.month === m.month;
  return (
    <div className="flex-1 flex flex-col items-center justify-end relative" data-testid={`monthly-bar-${m.label}`}>
      {m.revenue > 0 ? (
        <div className="text-[10px] font-mono text-slate-600 mb-1 whitespace-nowrap">{fmtINRCompact(m.revenue)}</div>
      ) : null}
      <div
        className="w-full rounded-t-md transition-all"
        style={{
          height: `${heightPct}%`,
          minHeight: m.revenue > 0 ? 4 : 0,
          backgroundColor: isCurrent ? PRIMARY : '#9FE1CB',
        }}
      />
      <div className="text-[10px] text-slate-500 mt-1.5 font-medium">{m.label}</div>
    </div>
  );
};

const HeadlineCard = ({ label, value, subValue, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-lg font-semibold text-slate-900 mt-1 truncate">{value}</div>
    {subValue ? <div className="text-[10px] text-slate-400 mt-0.5">{subValue}</div> : null}
  </Card>
);

const _formatYoY = (yoyPct) => {
  if (yoyPct === null || yoyPct === undefined) return '—';
  return `${yoyPct > 0 ? '+' : ''}${yoyPct}%`;
};

const MonthlyRevenueTab = () => {
  const [fy, setFy] = useState(CURRENT_FY);
  const [data, setData] = useState({ months: [], target: 500000, bestMonth: null, avgMonthly: 0, fyTotal: 0, yoyPct: null, currentMonth: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/analytics/monthly-revenue?fy=${fy}`)
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => toast.error('Failed to load monthly revenue'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fy]);

  const maxValue = useMemo(() => {
    const max = Math.max(data.target || 0, ...data.months.map((m) => m.revenue || 0));
    // Pad 15% so the tallest bar doesn't kiss the top of the chart.
    return Math.max(1, max * 1.15);
  }, [data.months, data.target]);

  const chartHeight = 240;
  const targetY = chartHeight - (data.target / maxValue) * chartHeight;
  const yoySub = data.yoyPct === null || data.yoyPct === undefined ? 'no prior FY data' : null;

  // Hoist to identifiers — see DestinationsTab note on Babel plugin recursion.
  const months = data.months;
  const currentMonth = data.currentMonth;
  const target = data.target;
  const bestMonth = data.bestMonth;
  const avgMonthly = data.avgMonthly;
  const fyTotal = data.fyTotal;
  const yoyPct = data.yoyPct;

  return (
    <div className="space-y-5" data-testid="monthly-revenue-tab">
      <div className="flex justify-end">
        <FYSelect value={fy} onValueChange={setFy} testid="monthly-fy-select" />
      </div>

      <Card className="p-5" data-testid="monthly-revenue-chart">
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
        ) : (
          <div className="relative" style={{ height: chartHeight + 30 }}>
            <div
              className="absolute left-0 right-0 border-t border-dashed border-slate-300 flex items-center"
              style={{ top: targetY }}
              data-testid="monthly-target-line"
            >
              <span className="ml-auto text-[10px] font-mono text-slate-500 bg-white px-1.5 -mt-2">
                Target {fmtINRCompact(target)}
              </span>
            </div>
            <div className="flex items-end justify-between gap-2 h-full pt-4">
              {months.map((m) => (
                <MonthBar key={`${m.year}-${m.month}`} m={m} maxValue={maxValue} current={currentMonth} />
              ))}
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HeadlineCard testid="monthly-best" label="Best month" value={bestMonth || '—'} />
        <HeadlineCard testid="monthly-avg" label="Avg monthly" value={fmtINR(avgMonthly)} />
        <HeadlineCard testid="monthly-fy-total" label="FY total" value={fmtINR(fyTotal)} />
        <HeadlineCard testid="monthly-yoy" label="YoY" value={_formatYoY(yoyPct)} subValue={yoySub} />
      </div>
    </div>
  );
};

export default MonthlyRevenueTab;
